import { useCallback } from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import { CookiesProvider } from "react-cookie";
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router-dom";

import CheckLessonStatus from "./components/CheckLessonStatus";
import ErrorFallback from "./components/ErrorFallback";
import LessonLayout from "./components/LessonLayout";
import {
  CancelledLesson,
  CompletedLesson,
  Conference,
  LandingPage,
  Lobby,
  NotFound,
  RecordedLesson,
} from "./components/pages";
import { FirebaseContextProvider } from "./hooks/useFirebaseAuth";
import VideoProvider from "./hooks/VideoProvider";
import useConnectionOptions from "./lib/useConnectionOptions";
import theme from "./theme";

function RootLayout() {
  const connectionOptions = useConnectionOptions();

  const handleError = useCallback(
    (error: Error) => console.error("Video Error:", error),
    []
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <CookiesProvider>
        <VideoProvider options={connectionOptions} onError={handleError}>
          <div
            style={{
              height: "100vh",
              backgroundColor: theme.palette.background.default
            }}
          >
            <FirebaseContextProvider>
              <Sentry.ErrorBoundary fallback={ErrorFallback}>
                <Outlet />
              </Sentry.ErrorBoundary>
            </FirebaseContextProvider>
          </div>
        </VideoProvider>
      </CookiesProvider>
    </ThemeProvider>
  );
}

// Data router configuration
const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <LandingPage />
      },
      ...(import.meta.env.VITE_PUBLIC_STAGE === "production"
        ? []
        : [
          {
            path: "sandbox",
            element: (
              <Navigate
                to="/sandbox-demo/lesson?sandbox=true&role=tutor"
                replace
              />
            ),
          },
        ]),
      {
        path: ":lessonId",
        element: <LessonLayout />,
        children: [
          {
            element: <CheckLessonStatus />,
            children: [
              {
                index: true,
                element: <Lobby />
              },
              {
                path: "lesson",
                element: <Conference />
              },
            ],
          },
          {
            path: "completed",
            element: <CompletedLesson />
          },
        ],
      },
      {
        path: "cancelled",
        element: <CancelledLesson />
      },
      {
        path: ":lessonId/recording",
        element: <LessonLayout />,
        children: [{
          index: true,
          element: <RecordedLesson />
        }],
      },
      {
        path: "*",
        element: <NotFound />
      },
    ],
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
